import { useQuery } from '@tanstack/react-query';
import { endpoints } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { PlayCircle, Film, Tv, Music } from 'lucide-react';
import { useNow } from '@/lib/hooks';

const REFETCH = 10_000;

export function JellyfinWidget() {
  const now = useNow(1_000);
  const q = useQuery({
    queryKey: ['jellyfin'],
    queryFn: endpoints.integrations.jellyfin,
    refetchInterval: REFETCH,
  });
  const data = q.data?.jellyfin;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PlayCircle className="h-3.5 w-3.5" />
          {data?.serverName ?? 'Jellyfin'}
        </CardTitle>
        {data && (
          <span className="text-xs text-muted-foreground">{data.version}</span>
        )}
      </CardHeader>
      <CardBody>
        {!data ? (
          <div className="text-sm text-muted-foreground">
            {q.isLoading ? 'loading…' : 'unavailable (check JELLYFIN_BASE_URL + API key)'}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <Tile label="Movies" value={data.counts.movies} icon={Film} />
              <Tile label="Series" value={data.counts.series} icon={Tv} />
              <Tile label="Episodes" value={data.counts.episodes} icon={Tv} />
              <Tile label="Songs" value={data.counts.songs} icon={Music} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">
                Active streams ({data.activeSessions.length})
              </div>
              {data.activeSessions.length === 0 ? (
                <div className="text-xs text-muted-foreground">no active playback</div>
              ) : (
                <ul className="space-y-1.5">
                  {data.activeSessions.map((s) => (
                    <li
                      key={s.id}
                      className="rounded-md border border-border bg-card/60 p-2 text-xs"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium truncate">
                          {s.nowPlayingItem?.SeriesName
                            ? `${s.nowPlayingItem.SeriesName} — ${s.nowPlayingItem.Name}`
                            : s.nowPlayingItem?.Name ?? 'unknown'}
                        </span>
                        <span className="text-muted-foreground whitespace-nowrap">
                          {s.userName}
                        </span>
                      </div>
                      {s.transcodingInfo && (
                        <div className="text-[10px] text-muted-foreground/70 mt-0.5">
                          {s.transcodingInfo.IsVideoDirect ? 'direct' : 'transcode'} ·{' '}
                          {s.transcodingInfo.VideoCodec}/{s.transcodingInfo.AudioCodec}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex items-center justify-end text-xs text-muted-foreground">
              <span>{q.data?.lastUpdated ? relTime(q.data.lastUpdated, now) : '—'}</span>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function Tile({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: number;
  icon: typeof Film;
}) {
  return (
    <div className="rounded-md border border-border p-2">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className="text-lg font-semibold tabular-nums mt-0.5">{value.toLocaleString()}</div>
    </div>
  );
}

function relTime(iso: string, now: number): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  const diff = Math.max(0, Math.floor((now - t) / 1000));
  if (diff === 0) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}
